import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';

import { ObjectItem } from 'api/clients/types';
import EmailNotificationBTN from 'components/ClientTable/EmailNotificationBTB';
import { useManageObjectModalV2Store } from 'components/ClientTable/Modals/ManageObjectModalV2/ManageObjectModalV2Store';
import { useManageDeleteObjModalV2Store } from 'components/ClientTable/Modals/DeleteObjModal/useManageDeleteObjModalV2';
import { useAddTechnicianModalStore } from 'components/ClientTable/Modals/AddTechnicanModal/AddTechnicianModalStore';
import { TrapTableType } from 'components/TrapsTables/trapTables';

interface Props {
  object_data: ObjectItem;
}

const ObjectData: React.FC<Props> = ({ object_data }) => {
  const { t } = useTranslation();
  const { onManageObjectModalV2Open } = useManageObjectModalV2Store();

  const traps = [
    { label: TrapTableType.MultiCatchMouseTraps, count: object_data.mct_count },
    { label: TrapTableType.SnapTrapRat, count: object_data.srst_count },
    { label: TrapTableType.SnapTrapMouse, count: object_data.smst_count },
    { label: TrapTableType.SmartRodenBaitModule, count: object_data.srbm_count },
  ].filter(item => item.count > 0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedObject, setSelectedObject] = useState<ObjectItem | undefined>(undefined);
  const { open: openAddTechnicianModal } = useAddTechnicianModalStore();
  const { onManageObjectDeleteModalV2Open } = useManageDeleteObjModalV2Store();
  return (
    <div className="font-poppins">
      <div className="d-flex text-black align-items-center border-bottom gap-3" style={{ minHeight: '40px' }}>
        <div className="px-2 py-2 fs-2 text-purple">{object_data.name || t('ClientTable.unnamed-object')}</div>
      </div>
      <div
        className="d-flex justify-content-between text-black align-items-center border-bottom gap-3"
        style={{ minHeight: '40px' }}
      >
        <div className="px-2 py-1 fw-bold fs-14">{t('ClientTable.titles.city')}</div>
        <div title={object_data.address} className="px-2 py-1 fs-14 text-nowrap text-ellipsis">
          {object_data.address}
        </div>
      </div>
      <div
        className="d-flex justify-content-between text-black align-items-center border-bottom gap-3"
        style={{ minHeight: '40px' }}
      >
        <div className="px-2 py-1 fw-bold fs-14">{t('ClientTable.titles.owner-site')}</div>
        <div className="px-2 py-1 fs-14 text-nowrap">
          {object_data.manager && (
            <div>
              {object_data.manager.first_name} {object_data.manager.last_name}
            </div>
          )}
        </div>
      </div>
      <div
        className="d-flex justify-content-between text-black align-items-center border-bottom gap-3"
        style={{ minHeight: '40px' }}
      >
        <div className="px-2 py-1 fw-bold fs-14">{t('ClientTable.titles.technician')}</div>
        <div className="py-1 fs-14">
          <div className="d-flex align-items-center justify-content-between">
            <div className="avatar-group">
              {object_data.technicians.map(tech => (
                <div
                  className={`avatar-group-item ${tech.is_lead && 'border-warning'} d-flex align-items-center`}
                  key={tech.technician.id}
                >
                  <img
                    src={`${tech.technician.avatar}`}
                    alt={tech.technician.last_name}
                    title={`${tech.technician.last_name} ${tech.technician.first_name}`}
                    className="rounded-circle avatar-xs"
                  />
                </div>
              ))}
            </div>
            <div>
              <Button
                onClick={() => {
                  openAddTechnicianModal(object_data.slug);
                }}
                type="button"
                className="btn btn-sm btn-rounded rounded-5 btn-soft-success custom-toggle waves-light ms-2"
                title={t('ClientTable.add-technician')}
              >
                <span className="icon-on fs-5">
                  <i className="ri-add-circle-fill align-bottom" />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      {traps.length > 0 && (
        <div
          className="d-flex justify-content-between text-black border-bottom gap-3 py-2"
          style={{ minHeight: '40px' }}
        >
          <div className="px-2 fw-bold fs-14">{t('ClientTable.titles.amount-traps')}</div>

          <div className="px-2 fs-12 text-nowrap d-flex flex-column gap-1 text-end">
            {traps.map(trap => (
              <span key={trap.label}>
                {trap.label}: <strong>{trap.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      <div
        className="d-flex justify-content-between text-black align-items-center border-bottom gap-3"
        style={{ minHeight: '40px' }}
      >
        <div className="px-2 py-1 fw-bold fs-14">{t('ClientTable.titles.actions')}</div>
        <div className="px-2 py-1 fs-14 ">
          <div
            className="btn-group btn-group-sm mt-4 mt-sm-0 d-flex justify-content-center align-items-center"
            role="group"
          >
            <button
              type="button"
              title={t('ClientTable.titles.helper-text')}
              className="btn  btn-ghost-primary btn-icon"
              onClick={() => {
                onManageObjectModalV2Open(object_data.id, object_data);
                setSelectedObject(object_data);
              }}
            >
              <i className="ri-pencil-fill icon icon__edit" />
            </button>
            <EmailNotificationBTN
              objectSlug={String(object_data.slug)}
              mct_receive_messages={object_data.email_messagess_send.mct_receive_messages}
              srbm_receive_messages={object_data.email_messagess_send.srbm_receive_messages}
              smst_receive_messages={object_data.email_messagess_send.smst_receive_messages}
              srst_receive_messages={object_data.email_messagess_send.srst_receive_messages}
              mct_count={object_data.mct_count}
              srbm_count={object_data.srbm_count}
              smst_count={object_data.smst_count}
              srst_count={object_data.srst_count}
            />
            <button
              title={t('ClientTable.titles.helper-delete-text')}
              onClick={() => {
                onManageObjectDeleteModalV2Open(object_data.id, t('Modals.deleteSiteModalTitle'), object_data.name);
                setSelectedObject(object_data);
              }}
              type="button"
              className="btn btn-ghost-primary btn-icon"
            >
              <span className="remove-item-btn">
                <i className="ri-delete-bin-6-fill icon icon__delete" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectData;
